import React, { useEffect, useState } from 'react';
import { Compass, FolderOpen, HelpCircle, Menu, PlusCircle, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMarketplaceAuth } from './MarketplaceAuthContext';
import ProfileMenu from './ProfileMenu';
import { TOUCH_TARGET } from './marketplaceUi';
import { labelForUseCase } from './useCaseMeta';
import { MARKETPLACE_LOGOUT_URL } from './marketplaceLinks';
// The app's real booking link (env-overridable) — the same one the login page and the
// free-tier wall use. Do NOT hardcode a second Calendly URL here.
import { BOOK_A_CALL_URL } from '../../config/constants';

const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// Grey canvas — the white cards and the sticky nav bar read as raised surfaces
// against it.
const PAGE_BG = 'bg-[#f9fafb]';

// Primary nav. "New study" is the default landing surface — the product is the study,
// not the dashboard.
type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  match: (path: string) => boolean;
};

// Primary nav. "New study" is the default surface — the product is the study, not a
// dashboard.
const NAV_ITEMS: NavItem[] = [
  { to: '/marketplace/run', label: 'New study', icon: PlusCircle, match: (p) => p.startsWith('/marketplace/run') },
  { to: '/marketplace/twins', label: 'Explore twins', icon: Compass, match: (p) => p.startsWith('/marketplace/twins') },
  { to: '/marketplace/history', label: 'My studies', icon: FolderOpen, match: (p) => p.startsWith('/marketplace/history') },
];

// Kept in its own group: support is a different KIND of destination from the three
// things you actually do here, and mixing it in blurs the primary nav.
const SUPPORT_ITEMS: NavItem[] = [
  { to: '/marketplace/faq', label: 'Help & FAQ', icon: HelpCircle, match: (p) => p.startsWith('/marketplace/faq') },
];


// Header icon buttons (hamburger, drawer close). The icon stays 20px — only the HIT AREA
// grows to the 44px touch minimum; the negative margin absorbs the extra width so the
// icon keeps its optical alignment with the container's padding.
const ICON_BUTTON =
  'flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-black/[0.04]';

// Per-page browser tab titles (#86) — so tabs/history/bookmarks are meaningful.
const TITLE_SUFFIX = 'BluePill Marketplace';
function titleForPath(path: string): string {
  if (path.endsWith('/run/build')) return 'Build your audience';
  if (path.endsWith('/run/fill')) return 'Set up study';
  if (path.endsWith('/run/review')) return 'Review & pay';
  if (path.endsWith('/run/processing')) return 'Running your study';
  if (path.endsWith('/run')) return 'Choose a study';
  if (path.endsWith('/history')) return 'Your studies';
  if (path.endsWith('/chat')) return labelForUseCase('chat');
  if (path.endsWith('/results')) return 'Study results';
  if (path.endsWith('/done')) return "What's next";
  return '';
}

/**
 * Chrome for the marketplace surface. Thin white top nav over a warm off-white
 * (#f5f5f3) page. The BluePill logo links out to the marketing site (opens a new
 * tab, so the in-progress study is never discarded); the in-app nav lives to the
 * right, with a logout action for signed-in users.
 */
const MarketplaceShell: React.FC<{ children: React.ReactNode; maxWidth?: string; bare?: boolean }> = ({
  children,
  maxWidth = 'max-w-[860px]',
  bare = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user } = useMarketplaceAuth();

  useEffect(() => {
    const label = titleForPath(location.pathname);
    document.title = label ? `${label} · ${TITLE_SUFFIX}` : TITLE_SUFFIX;
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    // Full external redirect to the public marketing marketplace — a logged-out user
    // should leave the app, not land on the in-app /marketplace shell.
    window.location.href = MARKETPLACE_LOGOUT_URL;
  };

  const [navOpen, setNavOpen] = useState(false);
  const isDesktop = typeof window !== 'undefined' && window.matchMedia?.('(min-width: 768px)').matches;

  // Close the drawer whenever the route changes — otherwise it stays open on top of the
  // page the user just navigated to.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  const NavButton: React.FC<{ item: NavItem }> = ({ item }) => {
    const current = item.match(location.pathname);
    const Icon = item.icon;
    return (
      <button
        onClick={() => navigate(item.to)}
        aria-current={current ? 'page' : undefined}
        className={[
          'flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-colors',
          TOUCH_TARGET,
          current
            ? 'bg-[#E8FCFA] text-[#0B7C74]'
            : 'text-[#6b7280] hover:bg-black/[0.04] hover:text-[#111]',
        ].join(' ')}
      >
        <Icon className="h-4 w-4 flex-shrink-0" />
        {item.label}
      </button>
    );
  };

  // In the MARKETPLACE the logo is an in-app home button: it goes to New study, the
  // product's default surface. It used to link out to the marketing site, which ejected
  // people from the app mid-flow. Only this shell changes — the public landing page and
  // the main platform keep their own behaviour.
  const logo = (
    <button
      onClick={() => navigate('/marketplace/run')}
      className="flex items-center overflow-hidden transition-opacity hover:opacity-80"
      aria-label="BluePill — new study"
    >
      {/* logo.png has large transparent top/bottom margins baked in; clip them with a
          negative vertical margin so the wordmark sits flush. */}
      <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="-my-4 h-20 w-auto object-contain" />
    </button>
  );

  // Logged out (the gate renders inside this shell too): no nav, no chrome — just the
  // logo over the canvas, so the signup page stays undistracted.
  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${PAGE_BG} text-[#111]`}>
        <header className="border-b border-black/10 bg-white">
          <div className="flex items-center px-4 py-3 md:px-8">{logo}</div>
        </header>
        <main className={`mx-auto ${maxWidth} px-4 pb-32 pt-6 md:px-8 md:pt-10`}>{children}</main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${PAGE_BG} text-[#111]`}>
      {/* The rail is FIXED on desktop and an off-canvas drawer below md. Content is
          pushed by the rail only from md up — on a phone it must use the full width. */}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 w-[260px] border-r border-black/10 bg-white transition-transform duration-200 md:z-20 md:w-[220px] md:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-hidden={!navOpen && !isDesktop}
      >
        <div className="flex h-[60px] items-center justify-between px-5">
          {logo}
          <button
            onClick={() => setNavOpen(false)}
            aria-label="Close menu"
            className={`${ICON_BUTTON} -mr-2.5 text-[#6b7280] md:hidden`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="mt-6 flex flex-col gap-1 px-3">
          {NAV_ITEMS.map((item) => <NavButton key={item.to} item={item} />)}

          <div className="mt-7 px-3 pb-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9ca3af]">
              Support
            </span>
          </div>
          {SUPPORT_ITEMS.map((item) => <NavButton key={item.to} item={item} />)}
        </nav>
      </aside>

      {/* Scrim: tapping outside the drawer closes it. Desktop never sees it. */}
      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          aria-hidden="true"
        />
      )}

      <div className="md:pl-[220px]">
        <header className="sticky top-0 z-10 border-b border-black/10 bg-white">
          <div className="flex h-[60px] items-center gap-3 px-4 md:px-6">
            {/* On mobile the rail is hidden, so the header carries the logo + hamburger. */}
            <button
              onClick={() => setNavOpen(true)}
              aria-label="Open menu"
              aria-expanded={navOpen}
              className={`${ICON_BUTTON} -ml-2 text-[#111] md:hidden`}
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="md:hidden">{logo}</span>

            <div className="ml-auto flex items-center gap-2 md:gap-3">
              {/* The demo CTA is the first thing to go when width is scarce — the account
                  menu is not, because Log out lives in it. */}
              <a
                href={BOOK_A_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden rounded-lg border border-black/15 bg-white px-3.5 py-2 text-[13px] font-medium text-[#111] transition-colors hover:bg-[#f3f4f6] sm:inline-flex"
              >
                Book a demo call
              </a>
              <ProfileMenu
                email={user?.email}
                name={user?.name}
                onStudies={() => navigate('/marketplace/history')}
                onLogout={onLogout}
              />
            </div>
          </div>
        </header>

        {/* `bare` lets a self-contained, full-screen form (the concept test) own the
            whole canvas. Otherwise the bottom padding clears the sticky WizardNav bar so
            the last card is never trapped under it. */}
        {bare ? (
          <main className="w-full">{children}</main>
        ) : (
          <main className={`mx-auto ${maxWidth} px-4 pb-32 pt-6 md:px-8 md:pt-10`}>{children}</main>
        )}
      </div>
    </div>
  );
};

export default MarketplaceShell;
